import React, { useState } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { releaseNotesButton_softwareReleasesVersionDetail$key } from '@atlassian/jira-relay/src/__generated__/releaseNotesButton_softwareReleasesVersionDetail.graphql';
import RELEASE_NOTES_QUERY, {
	type uiReleaseNotesModalQuery,
} from '@atlassian/jira-relay/src/__generated__/uiReleaseNotesModalQuery.graphql';
import { useCreateReleaseNotesExperienceStart } from '@atlassian/jira-software-releases-add-release-notes-modal/src/services/create-release-notes-experience-tracker/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import { NonCriticalErrorBoundary } from '../../../common/utils/non-critical-error-boundary.tsx';
import messages from './messages.tsx';
import { useRedirectAutoOpenReleaseModal } from './utils.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyReleaseNotesModal = lazy(() =>
	import(
		/* webpackChunkName: "async-release-note-modal" */ '@atlassian/jira-software-releases-add-release-notes-modal/src/ui/async.tsx'
	).then((module) => module.ReleaseNotesModal),
);

export const ReleaseNotesButton = ({
	jiraVersion,
}: {
	jiraVersion: releaseNotesButton_softwareReleasesVersionDetail$key;
}): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment releaseNotesButton_softwareReleasesVersionDetail on JiraVersion {
				id @required(action: THROW)
			}
		`,
		jiraVersion,
	);
	const [releaseNotesQueryReference, loadReleaseNotesQuery] =
		useQueryLoader<uiReleaseNotesModalQuery>(RELEASE_NOTES_QUERY);
	const loadReleaseNoteModalResource = () => {
		loadReleaseNotesQuery({ versionAri: version.id }, { fetchPolicy: 'store-and-network' });
		LazyReleaseNotesModal.preload();
	};
	useRedirectAutoOpenReleaseModal(() => setIsOpen(true), loadReleaseNoteModalResource);

	const { formatMessage } = useIntl();
	const experienceStart = useCreateReleaseNotesExperienceStart();

	return (
		<NonCriticalErrorBoundary eventLocation="releaseNotesModal" fallback="unmount">
			<Placeholder name="release-note-modal" fallback={null}>
				<ModalTransition>
					{releaseNotesQueryReference && isOpen && (
						<LazyReleaseNotesModal
							queryReference={releaseNotesQueryReference}
							onClose={() => setIsOpen(false)}
						/>
					)}
				</ModalTransition>
			</Placeholder>
			<Button
				onMouseEnter={loadReleaseNoteModalResource}
				onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
					setIsOpen(true);
					fireUIAnalytics(analyticsEvent, 'versionReleaseNotesModal');
					experienceStart();
				}}
			>
				{formatMessage(messages.releaseNotesButton)}
			</Button>
		</NonCriticalErrorBoundary>
	);
};
